import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { ThemeProvider } from "./theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { adminKeys } from "@/features/api";
import { ApiError } from "@/lib/api-client";

/**
 * Oturum ortada düşerse (401) `me` sıfırlanır → RequireAdmin login'e yönlendirir.
 * Eski `me` verisi tutulsaydı guard sayfayı açık bırakırdı.
 */
function handleUnauthorized(client: QueryClient, error: unknown) {
  if (!(error instanceof ApiError) || error.status !== 401) return;
  if (client.getQueryData(adminKeys.me) === undefined) return;

  toast.error("Oturumun sona erdi. Tekrar giriş yap.");
  client.resetQueries({ queryKey: adminKeys.me });
  client.removeQueries({
    predicate: (q) => q.queryKey[0] === "admin" && q.queryKey[1] !== adminKeys.me[1],
  });
}

function createQueryClient() {
  const client: QueryClient = new QueryClient({
    queryCache: new QueryCache({ onError: (error) => handleUnauthorized(client, error) }),
    mutationCache: new MutationCache({ onError: (error) => handleUnauthorized(client, error) }),
    defaultOptions: {
      queries: {
        staleTime: 30 * 1000,
        retry: (count, err) =>
          !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 1,
        refetchOnWindowFocus: true,
      },
    },
  });
  return client;
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [client] = useState(createQueryClient);

  return (
    <ThemeProvider>
      <QueryClientProvider client={client}>
        {children}
        <Toaster position="bottom-right" containerAriaLabel="Bildirimler" richColors />
      </QueryClientProvider>
    </ThemeProvider>
  );
}
